import { useState, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

interface PerformanceData {
  cpu: number;
  memory: number;
  storage: number;
  uptime: string;
}

interface SystemInfo {
  system_version?: string;
  database_version?: string;
  node_environment?: string;
  last_deployment?: string;
  active_sessions?: number | string;
  total_records?: number | string;
}

interface Metric {
  label: string;
  value: string;
  icon: string;
  color: string;
  bar: number;
}

export default function AdminMaintenancePage() {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);

  const getMetricColor = (value: number) => {
    if (value > 80) {
      return 'text-red-600';
    }

    if (value > 60) {
      return 'text-amber-600';
    }

    return 'text-teal-600';
  };

  const getBarColor = (value: number) => {
    if (value > 80) {
      return 'bg-red-500';
    }

    if (value > 60) {
      return 'bg-amber-500';
    }

    return 'bg-teal-500';
  };

  const buildMetrics = (data: PerformanceData): Metric[] => {
    return [
      {
        label: 'CPU Usage',
        value: `${data.cpu}%`,
        icon: 'ri-cpu-line',
        color: getMetricColor(data.cpu),
        bar: Math.min(Math.max(Number(data.cpu) || 0, 0), 100),
      },
      {
        label: 'Memory',
        value: `${data.memory}%`,
        icon: 'ri-server-line',
        color: getMetricColor(data.memory),
        bar: Math.min(Math.max(Number(data.memory) || 0, 0), 100),
      },
      {
        label: 'Storage',
        value: `${data.storage}%`,
        icon: 'ri-hard-drive-2-line',
        color: getMetricColor(data.storage),
        bar: Math.min(Math.max(Number(data.storage) || 0, 0), 100),
      },
      {
        label: 'Uptime',
        value: data.uptime || 'Unavailable',
        icon: 'ri-time-line',
        color: 'text-teal-600',
        bar: 100,
      },
    ];
  };

  useEffect(() => {
    let mounted = true;

    const fetchPerformance = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/get_system_performance.php`
        );

        if (!response.ok) {
          throw new Error(
            `Performance request failed: HTTP ${response.status}`
          );
        }

        const data: PerformanceData = await response.json();

        if (!mounted) return;

        setMetrics(buildMetrics(data));
      } catch (error) {
        console.error(
          'Failed to load system performance:',
          error
        );
      }
    };

    fetchPerformance();

    const interval = setInterval(
      fetchPerformance,
      5000
    );

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchSystemInfo = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/get_system_info.php`
        );

        if (!response.ok) {
          throw new Error(
            `System information request failed: HTTP ${response.status}`
          );
        }

        const data: SystemInfo = await response.json();

        if (!mounted) return;

        setSystemInfo(data);
      } catch (error) {
        console.error(
          'Failed to load system information:',
          error
        );
      }
    };

    fetchSystemInfo();

    return () => {
      mounted = false;
    };
  }, []);

  const systemInformation = [
    {
      label: 'System Version',
      value:
        systemInfo?.system_version ||
        'Loading...',
    },
    {
      label: 'Database Version',
      value:
        systemInfo?.database_version ||
        'Loading...',
    },
    {
      label: 'Node Environment',
      value:
        systemInfo?.node_environment ||
        'Loading...',
    },
    {
      label: 'Last Deployment',
      value:
        systemInfo?.last_deployment ||
        'Loading...',
    },
    {
      label: 'Active Sessions',
      value:
        systemInfo?.active_sessions ??
        'Loading...',
    },
    {
      label: 'Total Records',
      value:
        systemInfo?.total_records ??
        'Loading...',
    },
  ];

  return (
    <AppLayout
      title="Maintenance"
      breadcrumb={['Admin', 'Maintenance']}
    >
      <div className="space-y-6">

        {/* System Performance */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <i className="ri-pulse-line text-teal-500" />
            System Performance
          </h3>

          {metrics.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <i
                      className={`${metric.icon} ${metric.color}`}
                    />

                    <p className="text-xs text-gray-500">
                      {metric.label}
                    </p>
                  </div>

                  <p
                    className={`text-2xl font-bold ${metric.color} mb-2`}
                  >
                    {metric.value}
                  </p>

                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${getBarColor(
                        metric.bar
                      )}`}
                      style={{
                        width: `${metric.bar}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                'CPU Usage',
                'Memory',
                'Storage',
                'Uptime',
              ].map((label) => (
                <div
                  key={label}
                  className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-loader-4-line text-amber-600 animate-spin" />

                    <p className="text-xs text-gray-500">
                      {label}
                    </p>
                  </div>

                  <p className="text-2xl font-bold text-amber-600 mb-2">
                    Checking...
                  </p>

                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-amber-500 animate-pulse"
                      style={{
                        width: '100%',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* System Information */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-teal-500" />
            System Information
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {systemInformation.map((info) => (
              <div
                key={info.label}
                className="bg-gray-50 rounded-xl p-4 border border-gray-100"
              >
                <p className="text-xs text-gray-400 mb-1">
                  {info.label}
                </p>

                <p className="text-sm font-semibold text-gray-800 break-words">
                  {info.value}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </AppLayout>
  );
}